import axios from 'axios';
import { cloneDeep, filter, find, findIndex, sortBy } from 'lodash';
import { toast } from 'react-toastify';
import Rfi from 'types/Rfi';
import RfiElement from 'types/RfiElement';
import { Attachment, Element, ElementAnswer, ElementVendorQuestion, fieldTypes, RfiData, RfiStatus, SectionGroup, sectionNames } from 'types/RfiTypes';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

export interface RfiState {
  rfi: Rfi | null;
  describe: RfiElement[] | null;
  attachments: Attachment[] | null;
  vendorInfo: RfiElement[] | null;
  questionnaire: RfiElement[] | null;
  finalize: RfiElement[] | null;
  vendorQuestions: ElementVendorQuestion[] | null;
  isLoading: boolean | null;
  error?: string;
}

const initialState: RfiState = {
  rfi: null,
  describe: [],
  attachments: [],
  vendorInfo: [],
  questionnaire: [],
  vendorQuestions: [],
  finalize: [],
  isLoading: false,
};

interface INewVendorQuestion {
  rfxVendorQuestionId: number;
  rfiElementId: number | null;
  questionText: string;
  answerStatus: string;
  answerId: number;
}

const setRfiState = (state: RfiState, rfi: RfiData) => {
  state.isLoading = false;
  state.error = "";

  state.rfi = new Rfi(rfi);

  state.vendorQuestions = [];

  rfi.vendorQuestions &&
    rfi.vendorQuestions.forEach((question: ElementVendorQuestion) => {
      state.vendorQuestions &&
        state.vendorQuestions.push({
          ...question,
          section: "",
          fieldLabel: "",
        });
    });

  setVendorQuestions(state, rfi.elements);

  setSectionElements(state, rfi.elements, sectionNames.DESCRIBE);
  setSectionElements(state, rfi.elements, sectionNames.VENDOR_INFO);
  setSectionElements(state, rfi.elements, sectionNames.QUESTIONNAIRE);
  setSectionElements(state, rfi.elements, sectionNames.FINALIZE);

  if (rfi.attachments) {
    state.attachments = rfi.attachments;
  }
};

const setVendorQuestions = (state: RfiState, elements: Element[]) => {
  const detailSections = filter(elements, (e: Element) => {
    const eSections = e.sections?.find((s: SectionGroup) => {
      return s.sectionGroupName === sectionNames.DESCRIBE;
    });
    return eSections?.sectionGroupName === sectionNames.DESCRIBE;
  });
  if (detailSections) {
    detailSections.forEach((rfiElement: Element) => {
      let element = new RfiElement(rfiElement);
      element.vendorQuestions &&
        element.vendorQuestions.forEach((question: ElementVendorQuestion) => {
          state.vendorQuestions?.push({
            ...question,
            section: "",
            fieldLabel: element.fieldLabel,
          });
        });
    });
  }

  const vendorInfoSection = filter(elements, (e: Element) => {
    const eSections = e.sections?.find((s: SectionGroup) => {
      return s.sectionGroupName === sectionNames.VENDOR_INFO;
    });
    return eSections?.sectionGroupName === sectionNames.VENDOR_INFO;
  });
  if (vendorInfoSection) {
    vendorInfoSection.forEach((rfiElement: Element) => {
      let element = new RfiElement(rfiElement);
      let questionNumber = element.sections?.find(
        (x: SectionGroup) => x.sectionGroupName === sectionNames.VENDOR_INFO
      )?.questionNumber;
      element.vendorQuestions &&
        element.vendorQuestions.forEach((question: ElementVendorQuestion) => {
          state.vendorQuestions?.push({
            ...question,
            section: sectionNames.VENDOR_INFO,
            fieldLabel: element.fieldLabel,
            questionNumber: questionNumber,
          });
        });
    });
  }

  const questionnaireSections = filter(elements, (e: Element) => {
    const eSections = e.sections?.find((s: SectionGroup) => {
      return s.sectionGroupName === sectionNames.QUESTIONNAIRE;
    });
    return eSections?.sectionGroupName === sectionNames.QUESTIONNAIRE;
  });
  if (questionnaireSections) {
    questionnaireSections.forEach((rfiElement: Element) => {
      let element = new RfiElement(rfiElement);
      let questionNumber = element.sections?.find(
        (x: SectionGroup) => x.sectionGroupName === sectionNames.QUESTIONNAIRE
      )?.questionNumber;
      element.vendorQuestions &&
        (element.sections?.length ?? 0) === 1 &&
        element.vendorQuestions.forEach((question: ElementVendorQuestion) => {
          state.vendorQuestions?.push({
            ...question,
            section: sectionNames.QUESTIONNAIRE,
            fieldLabel: element.fieldLabel,
            questionNumber: questionNumber,
          });
        });
    });
  }
};

const setSectionElements = (
  state: RfiState,
  elements: Element[],
  section: string
) => {
  switch (section) {
    case sectionNames.DESCRIBE:
      const detailSections = filter(elements, (e: Element) => {
        const eSections = e.sections?.find((s: SectionGroup) => {
          return s.sectionGroupName === sectionNames.DESCRIBE;
        });
        return eSections?.sectionGroupName === sectionNames.DESCRIBE;
      });

      const sortedDetailsSections = sortBy(
        detailSections,
        (element: Element) => {
          return element.sections?.find(
            (x: SectionGroup) => x.sectionGroupName === sectionNames.DESCRIBE
          )?.sequence;
        }
      );

      let describeElements = [] as RfiElement[];

      if (sortedDetailsSections) {
        sortedDetailsSections.forEach((rfiElement: Element) => {
          let element = new RfiElement(rfiElement);
          describeElements.push(element);
        });
      }
      state.describe = describeElements;

      break;

    case sectionNames.VENDOR_INFO:
      const vendorInfoSections = filter(elements, (e: Element) => {
        const eSections = e.sections?.find((s: SectionGroup) => {
          return s.sectionGroupName === sectionNames.VENDOR_INFO;
        });
        return eSections?.sectionGroupName === sectionNames.VENDOR_INFO;
      });

      const sortVendorInfoSections = sortBy(
        vendorInfoSections,
        (element: Element) => {
          return element.sections?.find(
            (x: SectionGroup) => x.sectionGroupName === sectionNames.VENDOR_INFO
          )?.sequence;
        }
      );

      let vendorInfoElements = [] as RfiElement[];

      if (sortVendorInfoSections) {
        sortVendorInfoSections.forEach((rfiElement: Element) => {
          let element = new RfiElement(rfiElement);
          vendorInfoElements.push(element);
        });
      }
      state.vendorInfo = vendorInfoElements;
      break;

    case sectionNames.QUESTIONNAIRE:
      const questionnaireSections = filter(elements, (e: Element) => {
        const eSections = e.sections?.find((s: SectionGroup) => {
          return s.sectionGroupName === sectionNames.QUESTIONNAIRE;
        });
        return eSections?.sectionGroupName === sectionNames.QUESTIONNAIRE;
      });

      const sortQuestionnaireSections = sortBy(
        questionnaireSections,
        (element: Element) => {
          return element.sections?.find(
            (x: SectionGroup) =>
              x.sectionGroupName === sectionNames.QUESTIONNAIRE
          )?.sequence;
        }
      );

      let questionnaireElements = [] as RfiElement[];

      if (sortQuestionnaireSections) {
        sortQuestionnaireSections.forEach((rfiElement: Element) => {
          let element = new RfiElement(rfiElement);
          questionnaireElements.push(element);
        });
      }

      state.questionnaire = questionnaireElements;
      break;
    case sectionNames.FINALIZE:
      const finalizeSections = filter(elements, (e: Element) => {
        const eSections = e.sections?.find((s: SectionGroup) => {
          return s.sectionGroupName === sectionNames.FINALIZE;
        });
        return eSections?.sectionGroupName === sectionNames.FINALIZE;
      });

      const sortFinalizeSections = sortBy(
        finalizeSections,
        (element: Element) => {
          return element.sections?.find(
            (x: SectionGroup) => x.sectionGroupName === sectionNames.FINALIZE
          )?.sequence;
        }
      );

      let finalizeElements = [] as RfiElement[];

      if (sortFinalizeSections) {
        sortFinalizeSections.forEach((rfiElement: Element) => {
          let element = new RfiElement(rfiElement);
          finalizeElements.push(element);
        });
      }
      state.finalize = finalizeElements;

      break;
    default:
      break;
  }
};

const renumberQuestions = (state: RfiState) => {
  let questionNumber = 1;
  if (state.vendorInfo !== null) {
    let vi = cloneDeep(state.vendorInfo);
    vi.forEach((element: RfiElement, index: number) => {
      if (element?.sections) {
        const eSections = element?.sections?.find((s: SectionGroup) => {
          return s.sectionGroupName === sectionNames.VENDOR_INFO;
        });
        if (eSections) {
          if (
            element.fieldType === fieldTypes.SECTIONHEADER ||
            element.fieldType === fieldTypes.GUIDANCETEXT
          ) {
            eSections.questionNumber = undefined;
          } else {
            eSections.questionNumber = questionNumber;
            questionNumber++;
          }
        }
        state.vendorInfo = vi;
      }
    });
  }

  if (state.questionnaire !== null) {
    let q = cloneDeep(state.questionnaire);
    q.forEach((element: RfiElement, index: number) => {
      if (element?.sections) {
        const eSections = element?.sections?.find((s: SectionGroup) => {
          return s.sectionGroupName === sectionNames.QUESTIONNAIRE;
        });
        if (eSections) {
          if (
            element.fieldType === fieldTypes.SECTIONHEADER ||
            element.fieldType === fieldTypes.GUIDANCETEXT
          ) {
            eSections.questionNumber = undefined;
          } else {
            eSections.questionNumber = questionNumber;
            questionNumber++;
          }
        }
        state.questionnaire = q;
      }
    });
  }
};

export const fetchRfi = createAsyncThunk(
  "asyncRfi/fetchRfi",
  async (rfi: { rfiId: number; isTemplate: boolean }) => {
    let url = `/api/v1/Rfi/id/${rfi.rfiId}`;
    if (rfi.isTemplate) {
      url = `/api/v1/Rfi/template/${rfi.rfiId}`;
    }
    const res = await axios.get(url, {
      params: { includeElements: true },
    });
    const data = await res.data;
    return data;
  }
);

export const fetchRfiLink = createAsyncThunk(
  "asyncRfi/fetchRfiLink",
  async (rfiLink: string) => {
    axios.defaults.withCredentials = true;
    const res = await axios.get(`/api/v1/Rfi/${rfiLink}`, {
      params: { includeElements: true },
    });
    const data = await res.data;
    return data;
  }
);

export const saveElementValuesAsync = createAsyncThunk(
  "asyncRfi/saveElementValuesAsync",
  async (arg, { getState }) => {
    // <-- destructure getState method
    const state = getState() as RootState; // <-- invoke and access state object

    if (!state.vendorUser.email) {
      await axios
        .put(`/api/v1/rfi/`, {
          rfiId: state.rfi?.rfi?.rfiId ?? "",
          title: state.rfi?.rfi?.title ?? "",
          status: "",
          updateSectionGroupRequest: null,
          editor: 0,
        })
        .catch((error) => {
          console.log(error);
        });
    }

    let updateUrl = `/api/v1/elements/rfi/values`;
    if (
      state.rfi.rfi?.vendorResponseId &&
      state.rfi.rfi?.vendorResponseId > 0
    ) {
      updateUrl = `${updateUrl}?vendorResponseId=${state.rfi.rfi?.vendorResponseId}`;
    }

    const updateDescribe = filter(state.rfi?.describe, (e: RfiElement) => {
      return e.hasValueChanged;
    });

    updateDescribe.map(async (element: any) => {
      await axios
        .put(updateUrl, [
          { elementId: element.elementId, answers: element.answers },
        ])
        .then(() => {
          element.clearHasChanged();
        });
    });

    const updateVendorInfo = filter(state.rfi?.vendorInfo, (e: RfiElement) => {
      return e.hasValueChanged;
    });

    updateVendorInfo.map(async (element: any) => {
      await axios
        .put(updateUrl, [
          { elementId: element.elementId, answers: element.answers },
        ])
        .then(() => {
          element.clearHasChanged();
        });
    });

    const updateQuestionnaire = filter(
      state.rfi?.questionnaire,
      (e: RfiElement) => {
        return e.hasValueChanged;
      }
    );

    updateQuestionnaire.map(async (element: any) => {
      await axios
        .put(updateUrl, [
          { elementId: element.elementId, answers: element.answers },
        ])
        .then(() => {
          element.clearHasChanged();
        });
    });

    const updateFinalize = filter(state.rfi?.finalize, (e: RfiElement) => {
      return e.hasValueChanged;
    });

    updateFinalize.map(async (element: any) => {
      await axios
        .put(updateUrl, [
          { elementId: element.elementId, answers: element.answers },
        ])
        .then(() => {
          element.clearHasChanged();
        });
    });

    toast.success("Updated");
  }
);

export const rfiSlice = createSlice({
  name: "rfi",
  initialState,
  reducers: {
    clearState: (state) => {
      state = initialState;
    },

    setRfi: (state, action: PayloadAction<Rfi>) => {
      state.rfi = action.payload;
    },
    setElements: (
      state,
      action: PayloadAction<{ elements: RfiElement[]; section: string }>
    ) => {
      switch (action.payload.section) {
        case sectionNames.DESCRIBE:
          state.describe = action.payload.elements;
          break;
        case sectionNames.VENDOR_INFO:
          state.vendorInfo = action.payload.elements;
          break;
        case sectionNames.QUESTIONNAIRE:
          state.questionnaire = action.payload.elements;
          break;
        case sectionNames.FINALIZE:
          state.finalize = action.payload.elements;
          break;
        default:
          break;
      }
    },
    setNewElements: (
      state,
      action: PayloadAction<{ elements: Element[]; section: string }>
    ) => {
      setSectionElements(
        state,
        action.payload.elements,
        action.payload.section
      );
    },
    updateElement: (
      state,
      action: PayloadAction<{ element: RfiElement; section: string }>
    ) => {
      switch (action.payload.section) {
        case sectionNames.DESCRIBE:
          if (state.describe !== null) {
            state.describe = state.describe?.map((element, i) =>
              element.elementId === action.payload.element.elementId
                ? cloneDeep(action.payload.element)
                : element
            );
          }
          break;
        case sectionNames.VENDOR_INFO:
          if (state.vendorInfo !== null) {
            state.vendorInfo = state.vendorInfo?.map((element, i) =>
              element.elementId === action.payload.element.elementId
                ? action.payload.element
                : element
            );
          }
          break;
        case sectionNames.QUESTIONNAIRE:
          if (state.questionnaire !== null) {
            state.questionnaire = state.questionnaire?.map((element, i) =>
              element.elementId === action.payload.element.elementId
                ? cloneDeep(action.payload.element)
                : element
            );
          }
          break;
        case sectionNames.FINALIZE:
          if (state.finalize !== null) {
            state.finalize = state.finalize?.map((element, i) =>
              element.elementId === action.payload.element.elementId
                ? cloneDeep(action.payload.element)
                : element
            );
          }
          break;
        default:
          break;
      }
      renumberQuestions(state);
    },
    deleteElement: (
      state,
      action: PayloadAction<{ elementId: number; section: string }>
    ) => {
      switch (action.payload.section) {
        case sectionNames.DESCRIBE:
          if (state.describe !== null) {
            var newDescribe = state.describe?.filter((element) => {
              return element.elementId !== action.payload.elementId;
            });

            newDescribe?.forEach((element: RfiElement, index: number) => {
              const eSections = element.sections?.find((s: SectionGroup) => {
                return s.sectionGroupName === sectionNames.DESCRIBE;
              });

              if (eSections) eSections.sequence = index++;
            });

            state.describe = newDescribe;
          }
          break;
        case sectionNames.VENDOR_INFO:
          if (state.vendorInfo !== null) {
            var newVendorInfo = state.vendorInfo?.filter((element) => {
              return element.elementId !== action.payload.elementId;
            });

            newVendorInfo?.forEach((element: RfiElement, index: number) => {
              const eSections = element.sections?.find((s: SectionGroup) => {
                return s.sectionGroupName === sectionNames.VENDOR_INFO;
              });

              if (eSections) eSections.sequence = index++;
            });

            state.vendorInfo = newVendorInfo;
          }
          break;
        case sectionNames.QUESTIONNAIRE:
          if (state.questionnaire !== null) {
            var newQuestionnaire = state.questionnaire?.filter((element) => {
              return element.elementId !== action.payload.elementId;
            });

            newQuestionnaire?.forEach((element: RfiElement, index: number) => {
              const eSections = element.sections?.find((s: SectionGroup) => {
                return s.sectionGroupName === sectionNames.QUESTIONNAIRE;
              });

              if (eSections) eSections.sequence = index++;
            });

            state.questionnaire = newQuestionnaire;
          }
          break;
        case sectionNames.FINALIZE:
          if (state.finalize !== null) {
            var newFinalize = state.finalize?.filter((element) => {
              return element.elementId !== action.payload.elementId;
            });

            newFinalize?.forEach((element: RfiElement, index: number) => {
              const eSections = element.sections?.find((s: SectionGroup) => {
                return s.sectionGroupName === sectionNames.FINALIZE;
              });

              if (eSections) eSections.sequence = index++;
            });

            state.finalize = newFinalize;
          }
          break;
        default:
          break;
      }

      renumberQuestions(state);
    },
    moveElement: (
      state,
      action: PayloadAction<{
        elementId: number;
        targetElementId: number;
        position: string;
        section: string;
      }>
    ) => {
      var el: RfiElement[] = [];
      switch (action.payload.section) {
        case sectionNames.DESCRIBE:
          if (state.describe !== null) {
            el = cloneDeep(state.describe);
          }
          break;
        case sectionNames.VENDOR_INFO:
          if (state.vendorInfo !== null) {
            el = cloneDeep(state.vendorInfo);
          }
          break;
        case sectionNames.QUESTIONNAIRE:
          if (state.questionnaire !== null) {
            el = cloneDeep(state.questionnaire);
          }
          break;
        case sectionNames.FINALIZE:
          if (state.finalize !== null) {
            el = cloneDeep(state.finalize);
          }
          break;
        default:
          break;
      }

      const fromIndex = findIndex(el, (element: RfiElement) => {
        return element.elementId === action.payload.elementId;
      });
      if (fromIndex < 0 || fromIndex >= el.length) {
        console.log("Error moving element - fromIndex out of range");
        return;
      }

      if (el) {
        // Remove element to move
        const elementToMove = el.splice(fromIndex, 1)[0];

        let toIndex = 0;
        if (action.payload.position === "after") {
          toIndex =
            Math.min(
              findIndex(el, (element: RfiElement) => {
                return element.elementId === action.payload.targetElementId;
              }),
              el.length
            ) + 1;
        } else if (action.payload.position === "before") {
          toIndex = Math.max(
            findIndex(el, (element: RfiElement) => {
              return element.elementId === action.payload.targetElementId;
            }),
            0
          );
        }

        // Reinsert element at new location
        el.splice(toIndex, 0, elementToMove);

        // Renumber sequences in section sub-object
        el.forEach((element: RfiElement, index: number) => {
          const section = element.sections?.find((s: SectionGroup) => {
            return s.sectionGroupName === action.payload.section;
          });

          if (section) {
            section.sequence = index;
          }
          return element;
        });

        setSectionElements(state, el, action.payload.section);
        if (
          action.payload.section === sectionNames.VENDOR_INFO ||
          action.payload.section === sectionNames.QUESTIONNAIRE
        ) {
          renumberQuestions(state);
        }
      }
    },
    setAttachments: (state, action: PayloadAction<Attachment[]>) => {
      state.attachments = action.payload;
    },

    setOwnerId: (
      state,
      action: PayloadAction<{ ownerId: number; currentUserId: string }>
    ) => {
      if (state.rfi) {
        if (state.rfi.ownedById !== action.payload.ownerId) {
          state.rfi.isOwner = 0;
        }
        if (
          action.payload.currentUserId === action.payload.ownerId.toString()
        ) {
          state.rfi.isOwner = 1;
        }
        state.rfi = { ...state.rfi, ownedById: action.payload.ownerId };
      }
    },

    setRfiTitle: (state, action: PayloadAction<any>) => {
      if (state.rfi) {
        state.rfi = { ...state.rfi, title: action.payload };
      }
    },

    setElementValues: (
      state,
      action: PayloadAction<{ elementId: number; values: ElementAnswer[] }>
    ) => {
      let index = findIndex(state.describe, (element: RfiElement) => {
        return element.elementId === action.payload.elementId;
      });
      if (index >= 0 && state.describe) {
        const stateElement = state.describe[index];
        if (stateElement.mapsToField === "QA_CLOSE_DATE") {
          if (state.rfi) {
            state.rfi = {
              ...state.rfi,
              qaCloseDate: action.payload.values[0].value ?? "",
            };
          }
        }
        state.describe = state.describe.map((element, i) => {
          if (element.elementId === action.payload.elementId) {
            const newElement = cloneDeep(element);
            newElement.answers = action.payload.values;
            if (element.fieldType !== fieldTypes.ATTACHMENT) {
              newElement.hasValueChanged = true;
            }
            return newElement;
          } else {
            return element;
          }
        });
      }

      index = findIndex(state.vendorInfo, (element: RfiElement) => {
        return element.elementId === action.payload.elementId;
      });
      if (index >= 0 && state.vendorInfo) {
        state.vendorInfo = state.vendorInfo.map((element, i) => {
          if (element.elementId === action.payload.elementId) {
            const newElement = cloneDeep(element);
            newElement.answers = action.payload.values;
            if (element.fieldType !== fieldTypes.ATTACHMENT) {
              newElement.hasValueChanged = true;
            }
            return newElement;
          } else {
            return element;
          }
        });
      }

      index = findIndex(state.questionnaire, (element: RfiElement) => {
        return element.elementId === action.payload.elementId;
      });
      if (index >= 0 && state.questionnaire) {
        state.questionnaire = state.questionnaire.map((element, i) => {
          if (element.elementId === action.payload.elementId) {
            const newElement = cloneDeep(element);
            newElement.answers = action.payload.values;
            if (element.fieldType !== fieldTypes.ATTACHMENT) {
              newElement.hasValueChanged = true;
            }
            return newElement;
          } else {
            return element;
          }
        });
      }

      index = findIndex(state.finalize, (element: RfiElement) => {
        return element.elementId === action.payload.elementId;
      });

      if (index >= 0 && state.finalize) {
        const stateElement = state.finalize[index];
        if (stateElement.mapsToField === "RESPONSES_DUE_DATE") {
          if (state.rfi) {
            state.rfi = {
              ...state.rfi,
              responsesDueDate: action.payload.values[0].value ?? "",
            };
          }
        }
        state.finalize = state.finalize.map((element, i) => {
          if (element.elementId === action.payload.elementId) {
            const newElement = cloneDeep(element);
            newElement.answers = action.payload.values;
            if (element.fieldType !== fieldTypes.ATTACHMENT) {
              newElement.hasValueChanged = true;
            }
            return newElement;
          } else {
            return element;
          }
        });
      }
    },

    setRfiStatus: (state, action: PayloadAction<any>) => {
      if (state.rfi) {
        state.rfi = { ...state.rfi, status: action.payload };
        if (action.payload === RfiStatus.POSTED) {
          var thisVersion = state.rfi.versions?.find((v) => {
            return v.versionNumber === state.rfi?.versionNumber;
          });
          if (thisVersion) {
            thisVersion.status = action.payload;
          }
          if (state.vendorQuestions) {
            state.vendorQuestions = state.vendorQuestions?.map((question, i) =>
              question.answerStatus === "ReadyToPost"
                ? {
                    ...question,
                    answerStatus: "Posted",
                  }
                : question
            );
          }
        }
      }
    },

    updateVendorAnswer: (state, action: PayloadAction<any>) => {
      if (state.vendorQuestions) {
        state.vendorQuestions = state.vendorQuestions?.map((question, i) =>
          question.rfxVendorQuestionId === action.payload.rfxVendorQuestionId
            ? action.payload.answerStatus
              ? {
                  ...question,
                  answerText: action.payload.answerText,
                  answerStatus: action.payload.answerStatus,
                }
              : { ...question, answerText: action.payload.answerText }
            : question
        );
      }
    },

    upvoteVendorQuestion: (state, action: PayloadAction<number>) => {
      if (state.vendorQuestions) {
        state.vendorQuestions = state.vendorQuestions?.map((question, i) =>
          question.rfxVendorQuestionId === action.payload
            ? {
                ...question,
                didIVote: "true",
                voteCount: question.voteCount + 1,
              }
            : question
        );
      }
    },

    addVendorQuestion: (state, action: PayloadAction<INewVendorQuestion>) => {
      let element: RfiElement | null = null;
      let section: string = "";
      let questionNumber = 0;

      if (action.payload.rfiElementId) {
        if (state.describe) {
          let index = findIndex(state.describe, (element: RfiElement) => {
            return element.elementId === action.payload.rfiElementId;
          });
          if (index >= 0) {
            element = state.describe[index];
            section = sectionNames.DESCRIBE;
          }
        }
        if (state.vendorInfo) {
          let index = findIndex(state.vendorInfo, (element: RfiElement) => {
            return element.elementId === action.payload.rfiElementId;
          });
          if (index >= 0) {
            element = state.vendorInfo[index];
            section = sectionNames.VENDOR_INFO;
            //questionNumber = element.sections?.find((x:SectionGroup) => x.sectionGroupName === sectionNames.VENDOR_INFO)?.questionNumber ?? 0;
            const elementSection = find(
              element.sections,
              (x: SectionGroup) =>
                x.sectionGroupName === sectionNames.VENDOR_INFO
            );
            if (elementSection) {
              questionNumber = elementSection.questionNumber ?? 0;
            }
          }
        }
        if (state.questionnaire) {
          let index = findIndex(state.questionnaire, (element: RfiElement) => {
            return element.elementId === action.payload.rfiElementId;
          });
          let vendorQuestionIndex = findIndex(
            state.vendorInfo,
            (element: RfiElement) => {
              return element.elementId === action.payload.rfiElementId;
            }
          );
          if (index >= 0 && vendorQuestionIndex === -1) {
            element = state.questionnaire[index];
            section = sectionNames.QUESTIONNAIRE;
            const elementSection = find(
              element.sections,
              (x: SectionGroup) =>
                x.sectionGroupName === sectionNames.QUESTIONNAIRE
            );
            if (elementSection) {
              questionNumber = elementSection.questionNumber ?? 0;
            }
            // questionNumber = element.sections?.find((x:SectionGroup) => x.sectionGroupName === sectionNames.QUESTIONNAIRE)?.questionNumber;
          }
        }
      }

      let newQuestion = {
        rfxVendorQuestionId: action.payload.answerId,
        versionNumber: state.rfi?.versionNumber,
        questionText: action.payload.questionText,
        answerText: "",
        answerStatus: action.payload.answerStatus,
        voteCount: 0,
        isMine: "true",
        didIVote: "true",
        section: section,
        questionNumber: questionNumber,
        fieldLabel: element?.fieldLabel,
      } as ElementVendorQuestion;

      state.vendorQuestions?.push(newQuestion);
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchRfi.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchRfi.fulfilled, (state, action) => {
      setRfiState(state, action.payload);
    });
    builder.addCase(fetchRfi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(fetchRfiLink.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchRfiLink.fulfilled, (state, action) => {
      setRfiState(state, action.payload);
    });
    builder.addCase(fetchRfiLink.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
  },
});

export const {
  clearState,
  setRfi,
  setRfiTitle,
  setRfiStatus,
  setElements,
  setNewElements,
  updateElement,
  deleteElement,
  moveElement,
  setAttachments,
  setElementValues,
  setOwnerId,
  updateVendorAnswer,
  upvoteVendorQuestion,
  addVendorQuestion,
} = rfiSlice.actions;

export const selectRfi = (state: RootState) => state.rfi;
export const selectRfiState = (state: RootState) => state;

export default rfiSlice.reducer;
